/**
 * State 8 of 9 — retry action (spec 01 §18, `PRODUCT.md` §4).
 *
 * **Driven by:** no single response. It is the affordance attached to a call that
 * failed for a reason trying again could fix — a transport failure, any `5xx`
 * (`WORKFORCE_SERVICE_UNAVAILABLE`, `WORKFORCE_UPSTREAM_FAILED`,
 * `WORKFORCE_UPSTREAM_TIMEOUT`, `WORKFORCE_CIRCUIT_OPEN`), or a `429`. `isRetryable`
 * in `../api/errors` is the predicate, and it deliberately excludes refusals: a
 * retry button under a permission denial invites someone to hammer an endpoint that
 * is answering correctly.
 *
 * Spec 01 §18 asks for "a way to try again **that does not require a page reload**",
 * which is why this takes a handler rather than rendering a link: the screen refetches
 * and re-renders, and a session, a scroll position and a half-filled filter survive.
 *
 * ── On `retryAfterSeconds` ─────────────────────────────────────────────────
 *
 * A `429` carries `details.retry_after_seconds`. The button stays **enabled** and the
 * number is rendered as a hint, rather than the button being disabled for that long.
 * Two reasons: a control that is dead for sixty seconds with no countdown is
 * indistinguishable from one that is broken, and a countdown ticking on screen is
 * exactly the kind of motion `DESIGN.md` §9 rules out. Pressing early gets another
 * `429` — the server's answer, which is the one that is authoritative anyway.
 */

import type { ReactNode } from 'react';

import { useT } from '../i18n';
import { Icon } from './icons';

export interface RetryActionProps {
  readonly onRetry: () => void;
  /** True while the retried request is in flight. Disables the button and swaps the label. */
  readonly pending?: boolean;
  readonly label?: string;
  /** `details.retry_after_seconds` from a `429`, when the server sent one. */
  readonly retryAfterSeconds?: number | null;
  readonly testId?: string;
}

export function RetryAction({
  onRetry,
  pending = false,
  label,
  retryAfterSeconds = null,
  testId = 'state-retry',
}: RetryActionProps): ReactNode {
  const t = useT();
  return (
    <span className="wp-stack">
      <button
        type="button"
        className="wp-button"
        onClick={onRetry}
        disabled={pending}
        data-testid={testId}
      >
        <Icon name="refresh" />
        {pending ? t('common.retrying') : (label ?? t('common.retry'))}
      </button>
      {retryAfterSeconds === null ? null : (
        <span className="wp-small wp-muted">
          {t('common.retryAfter', { seconds: Math.ceil(retryAfterSeconds) })}
        </span>
      )}
    </span>
  );
}
